import React, { memo, useCallback, useMemo, useState } from 'react';
import { RootState, useTypedSelector } from 'lib/centralStore';
import { gtag, isMatch, isTruthy } from 'utility/functions';
import { useDynamicTempate, useFilteredAvvenimentoByKey, useInfoTipo } from 'features/sport/hooks';

import { AmbienteContext } from 'context/Ambiente';
import { KeyManagerSport } from 'features/sport/utils/keyManager';
import { SportManifestazioniTabs } from 'features/sport/components/SportManifestazioniTabs';
import { SportScommesseRenderConditional } from 'features/sport/components/sportScommesseRenderConditional';
import { SportsTopAntepostDto } from 'types/swagger';
import { Wrapper } from 'components/wrapper';
import { breakpoint } from 'utility/constant';
import styles from './SportAntepost.module.scss';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'hooks/useLingUI';
import useWindowSize, { enComparer } from 'hooks/useWindowSize';

export type SportAntepostProps = {
  title: string;
  isTopTemplate?: boolean;
  isTemplateManifestazione?: boolean;
};

const MySportAntepost = memo(function SportAntepost({ title, isTemplateManifestazione }: SportAntepostProps) {
  const { t } = useTranslation();
  const { slug, template: items } = useDynamicTempate<Array<SportsTopAntepostDto>>();

  const [currentTab, setCurrentTab] = useState<string>('1');
  const { isOpen } = useSelector((store: RootState) => store.carrello);
  const selectedTab = items?.[+currentTab - 1];

  const { tipoScommessaId } = new KeyManagerSport(selectedTab?.keyScommessa);
  const infoTipo = useInfoTipo(tipoScommessaId);

  const { doCheck } = useWindowSize();
  const [isMobile, isDesktopXl] = doCheck([
    { comparer: enComparer.less, size: breakpoint.md },
    { comparer: enComparer.greater, size: breakpoint.xl },
  ]);

  const filtredKeys = useFilteredAvvenimentoByKey(
    items?.map((item) => new KeyManagerSport(item.keyScommessa).avvenimentoKey)
  );

  const tabList = useMemo(() => {
    const result = structuredClone(items);

    for (let idx = (result ?? []).length; idx > 0; ) {
      idx--;
      if (!filtredKeys.some((key) => isMatch(result[idx].keyScommessa, `^${key}-`))) {
        result.splice(idx, 1);
      }
    }

    return result;
  }, [filtredKeys, items]);

  const getCurrentTab = useCallback(
    (value: string) => {
      setCurrentTab(value);
    },
    [setCurrentTab]
  );

  if (!isTruthy(tabList?.length)) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <React.Fragment key={`AntePost-${slug}`} />;
  }

  return (
    <Wrapper
      key={`Sport-AntePost-${slug}-${(items ?? []).length}`}
      bgColorTransparent
      noPaddingRight={isMobile || isOpen || isDesktopXl}
      noPaddingLeft
      noPaddingTop
    >
      <div className={styles.container}>
        <div className={styles.titleContainer}>
          {isTemplateManifestazione ? (
            <React.Fragment>
              <span className={styles.headline}>Top Antepost</span>
              <h2 className={styles.title}>{title}</h2>
            </React.Fragment>
          ) : (
            <h2 className={styles.title}>{title}</h2>
          )}
        </div>
        <SportManifestazioniTabs
          key={`Tabs-${slug}`}
          listTab={tabList}
          className={styles.backgroundTabContent}
          currentTab={currentTab}
          handleClick={getCurrentTab}
        >
          {+selectedTab.idTab === Number(currentTab) - 1 && (
            <strong className={styles.scommessaType} data-testid="SportAntepost" suppressHydrationWarning>
              {t(infoTipo?.descrizioneTrKey, infoTipo?.descrizione)}
            </strong>
          )}
          <SportScommesseRenderConditional
            scommessaKey={selectedTab.keyScommessa}
            hasTitle
            isAntepost
            gtagAction={() => gtag({ section: slug, detail: 'Top Antepost', event: 'Click quota' }, true)}
          />
        </SportManifestazioniTabs>
      </div>
    </Wrapper>
  );
});

export const SportAntepost = (props: SportAntepostProps) => {
  const { isReady, isFallback, asPath } = useRouter();

  return (
    <AmbienteContext.Provider
      key={`content-next-events`}
      value={{
        ambiente: 'sport',
        pathScommessa: ['sport', 'topAntePost', 'scommesse'],
      }}
    >
      <MySportAntepost {...props} key={`MySportAntepost-${isReady}-${isFallback}-${asPath}`} />
    </AmbienteContext.Provider>
  );
};